/* --------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

import Vue from 'vue';
import VueRouter from 'vue-router';
import VueResource from 'vue-resource';
import VueAnnouncer from 'vue-announcer';
import _ from 'lodash';
import moment from 'moment';

import App from './App';
import store from './vuex/store';
import routes from './config/routes';
import globalComponents from './components/utils/globals';
import globalFilters from './filters';
import Raven from 'raven-js';
import RavenVue from 'raven-js/plugins/vue';

// config setup
store.dispatch('loadAppConfig');

// router setup
Vue.use(VueRouter);
const router = getRouter();

// vue-resource setup
Vue.use(VueResource);
Vue.http.interceptors.push(getAuthInterceptor());
Vue.http.options.root = store.getters.appConfig.api.root;

// global components / filters
_.forOwn(globalComponents, (component, tag) => {
  Vue.component(tag, component);
});
_.forOwn(globalFilters, (filter, name) => {
  Vue.filter(name, filter);
});

// Set up Sentry
// For now, it's disabled locally
if (process.env.NODE_ENV === 'production') {
  Raven
    .config(
      window.VV.sentry.url,
      {release: window.VV.sentry.release}
    )
    .addPlugin(RavenVue, Vue)
    .install();
  window.Raven = Raven; // Make Raven globally accessible
}

Vue.use(VueAnnouncer, {complimentRoute: 'is ready'}, router);

router.beforeEach((to, from, next) => {
  document.title = 'VoiceVibes ' + to.meta.announcer;
  next();
});

// and load!
if (!window.location.search.match(/\bkey=/)) {
  store.dispatch('signInFromLocalStorage');
}

Object.defineProperty(Vue.prototype, 'moment', { value: moment });
Object.defineProperty(Vue.prototype, '_', { value: _ });

new Vue({
  el: 'app',
  store,
  router,
  render: h => h(App)
});

/***********************
 * functions
 */

function getRouter() {
  const router = new VueRouter({
    mode: 'history',
    scrollBehavior: function (to, from, savedPosition) {
      return savedPosition || { x: 0, y: 0 };
    },
    routes
  });

  router.beforeEach((to, from, next) => {
    if (to.meta.requiresAuth === true && !store.getters.isAuthenticated) {
      return next({ name: 'sign-in', query: { next: to.path } });
    }
    if (to.meta.signOut === true) {
      store.dispatch('signOut');
    }
    store.dispatch('clearNotification');
    next();
  });

  return router;
}

function getAuthInterceptor() {
  return (request, next) => {
    if (store.getters.isAuthenticated) {
      request.headers.set('Authorization', 'Bearer ' + [ store.getters.authOrgCode, store.getters.authApiKey ].join('-'));
    }
    next((response) => {
      if (store.getters.isAuthenticated) {
        if (response.status === 401) {
          store.dispatch('signOut');
          router.push({ name: 'sign-in', query: { next: router.currentRoute.path } });
          return false;
        }
        if (response.status === 402 && (!store.getters.org || store.getters.org.roles.indexOf('manager') === -1)) {
          router.push({ name: 'subscription' });
          return false;
        }
      }
    });
  };
}
